/** Units offered on the quantity stepper (docs/PROGRESS.md open question: confirm list). */
export const UNITS = ['pcs', 'kg', 'g', 'L', 'ml', 'dozen', 'pack', 'bunch'] as const;
export type Unit = (typeof UNITS)[number];

export function isUnit(value: string | null | undefined): value is Unit {
  return (UNITS as readonly string[]).includes(value ?? '');
}

/** How much one tap on − / + changes the quantity (no unit picked yet: 1). */
export function quantityStep(unit: Unit | null): number {
  if (unit === 'g' || unit === 'ml') return 100;
  if (unit === 'kg' || unit === 'L') return 0.5;
  return 1;
}

const MAX_QUANTITY = 99_999;

/**
 * A quantity typed by hand. Keeps what is on screen (so "1." can become "1.5") and the number
 * it means. Urdu digits (۱۲۳) become 123; at most one decimal point and 3 decimals (the
 * server stores 3); anything else is dropped.
 */
export function parseQuantity(typed: string): { text: string; value: number } {
  let text = '';
  let dot = false;
  let decimals = 0;
  for (const raw of typed) {
    const urdu = '۰۱۲۳۴۵۶۷۸۹'.indexOf(raw);
    const arabic = '٠١٢٣٤٥٦٧٨٩'.indexOf(raw);
    const ch = urdu >= 0 ? String(urdu) : arabic >= 0 ? String(arabic) : raw === '٫' || raw === ',' ? '.' : raw;
    if (ch === '.') {
      if (dot) continue;
      dot = true;
      text += text === '' ? '0.' : '.';
    } else if (ch >= '0' && ch <= '9') {
      if (dot && decimals === 3) continue;
      if (dot) decimals += 1;
      text += ch;
    }
  }
  text = text.replace(/^0+(?=\d)/, ''); // "007" → "7"
  let value = Number(text || 0);
  if (value > MAX_QUANTITY) {
    value = MAX_QUANTITY;
    text = String(MAX_QUANTITY);
  }
  return { text, value };
}

export function stepQuantity(current: number, unit: Unit | null, direction: 1 | -1): number {
  const next = current + direction * quantityStep(unit);
  return Math.max(0, Math.round(next * 10) / 10);
}

const UNIT_WORDS: Record<string, Unit> = {
  pc: 'pcs', pcs: 'pcs', piece: 'pcs', pieces: 'pcs',
  kg: 'kg', kgs: 'kg', kilo: 'kg',
  g: 'g', gm: 'g', gram: 'g', grams: 'g',
  l: 'L', ltr: 'L', litre: 'L', liter: 'L', litres: 'L', liters: 'L',
  ml: 'ml',
  dozen: 'dozen', doz: 'dozen',
  pack: 'pack', packs: 'pack', packet: 'pack',
  bunch: 'bunch',
};

/**
 * The size written in an item's name, as a quantity and unit: "Milk 2 L" → 2 L, "20 L water" →
 * 20 L, "Chicken 1 kg" → 1 kg. A usual buy fills them in with one tap; null when the name has
 * no size (the spender then enters it).
 */
export function sizeFromName(name: string): { quantity: number; unit: Unit } | null {
  const m = /(?:^|\s)(\d+(?:[.,]\d+)?)\s*([a-z]+)\b/i.exec(name);
  if (!m?.[1] || !m[2]) return null;
  const unit = UNIT_WORDS[m[2].toLowerCase()];
  const quantity = Number(m[1].replace(',', '.'));
  return unit && quantity > 0 ? { quantity, unit } : null;
}
